import {
  ChangeDetectionStrategy,
  Component,
  Directive,
  ElementRef,
  Inject,
  Input,
  IterableChanges,
  IterableDiffer,
  IterableDiffers,
  OnChanges,
  OnDestroy,
  Optional,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';
import { SafeAny } from '@ngx-simple/core/types';
import { SimCellDefDirective, SimColumnDefDirective } from './cell';
import { SIM_TABLE } from './default-options';

@Directive({ selector: '[simCellOutlet]' })
export class SimCdkCellOutletDirective implements OnDestroy {
  constructor(public _viewContainer: ViewContainerRef) {
    SimCdkCellOutletDirective.mostRecentCellOutlet = this;
  }

  /**
   * 如果这是视图中呈现的最后一个出口，则在销毁静态属性之后从该引用中删除，以避免内存泄漏。
   * 静态属性，包含该类的最新构造实例。当使用createEmbeddedView创建每个SImHeaderRow和SimRow组件时，由SimTable使用。
   * 在创建其中一个组件后，此属性将提供一个句柄来提供该组件的单元格和上下文。init之后，SimCellOutlet将使用提供的上下文构造单元格。
   */
  static mostRecentCellOutlet: SimCdkCellOutletDirective | null = null;
  /** 要在此outlet的视图容器中呈现的单元格的有序列表 */
  cells: SimCellDefDirective[];

  /** 要提供给每个单元格的数据上下文 */
  context: any;

  ngOnDestroy() {
    // 如果这是视图中呈现的最后一个出口，则在销毁静态属性之后从该引用中删除，以避免内存泄漏。
    if (SimCdkCellOutletDirective.mostRecentCellOutlet === this) {
      SimCdkCellOutletDirective.mostRecentCellOutlet = null;
    }
  }
}

@Directive()
export abstract class BaseRowDef implements OnChanges {
  /** 要在该行上显示的列 */
  columns: Iterable<string>;

  /** 用于检查是否对列进行了任何更改 */
  protected _columnsDiffer: IterableDiffer<SafeAny>;

  constructor(public template: TemplateRef<SafeAny>, protected _differs: IterableDiffers) {}

  ngOnChanges(changes: SimpleChanges): void {
    // 如果不存在不同的列，则创建不同的新列。根据列属性的初始值或空数组(如果没有提供)对其进行初始化。
    if (!this._columnsDiffer) {
      const columns = (changes['columns'] && changes['columns'].currentValue) || [];
      this._columnsDiffer = this._differs.find(columns).create();
      this._columnsDiffer.diff(columns);
    }
  }

  /** 返回当前列和上一个差异的列之间的差，如果没有差异，则返回null。 */
  getColumnsDiff(): IterableChanges<any> | null {
    return this._columnsDiffer.diff(this.columns);
  }

  extractCellTemplate(column: SimColumnDefDirective): TemplateRef<any> {
    if (this instanceof SimHeaderRowDefDirective) {
      return column.headerCell.template;
    }
    if (this instanceof SimFooterRowDefDirective) {
      return column.footerCell.template;
    } else {
      return column.cell.template;
    }
  }
}

@Directive({
  selector: '[simHeaderRowDef]'
})
export class SimHeaderRowDefDirective<T> extends BaseRowDef implements OnChanges {
  @Input('simHeaderRowDef') columns: Iterable<string>;

  @Input('simHeaderRowDefSticky') sticky: boolean;

  constructor(template: TemplateRef<any>, _differs: IterableDiffers, @Inject(SIM_TABLE) @Optional() public _table?: any) {
    super(template, _differs);
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
  }
}

@Directive({
  selector: '[simFooterRowDef]'
})
export class SimFooterRowDefDirective<T> extends BaseRowDef implements OnChanges {
  @Input('simFooterRowDef') columns: Iterable<string>;

  @Input('simFooterRowDefSticky') sticky: boolean;

  constructor(template: TemplateRef<any>, _differs: IterableDiffers, @Inject(SIM_TABLE) @Optional() public _table?: any) {
    super(template, _differs);
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
  }
}

@Directive({
  selector: '[simRowDef]'
})
export class SimRowDefDirective<T> extends BaseRowDef {
  @Input('simRowDefColumns') columns: Iterable<string>;

  @Input() when: (index: number, rowData: T) => boolean;

  // 添加一个输入来提供一个开关函数，以确定是否应该使用此模板
  constructor(template: TemplateRef<any>, _differs: IterableDiffers, @Inject(SIM_TABLE) @Optional() public _table?: SafeAny) {
    super(template, _differs);
  }
}

@Component({
  // tslint:disable-next-line: component-selector
  selector: 'tr[sim-row]',
  template: '<ng-container simCellOutlet></ng-container>',
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.None
})
export class SimRowComponent {
  constructor(private _elementRef: ElementRef<HTMLTableRowElement>, private _renderer: Renderer2) {
    const nativeElement = this._elementRef.nativeElement;
    this._renderer.addClass(nativeElement, 'sim-table-row');
    this._renderer.setAttribute(nativeElement, 'role', 'row');
  }
}

@Component({
  // tslint:disable-next-line: component-selector
  selector: 'tr[sim-header-row]',
  template: '<ng-container simCellOutlet></ng-container>',
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.None
})
export class SimHeaderRowComponent {
  constructor(private _elementRef: ElementRef<HTMLTableRowElement>, private _renderer: Renderer2) {
    const nativeElement = this._elementRef.nativeElement;
    this._renderer.addClass(nativeElement, 'sim-table-header-row');
    this._renderer.setAttribute(nativeElement, 'role', 'row');
  }
}

@Component({
  // tslint:disable-next-line: component-selector
  selector: 'tr[sim-footer-row]',
  template: '<ng-container simCellOutlet></ng-container>',
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.None
})
export class SimFooterRowComponent {
  constructor(private _elementRef: ElementRef<HTMLTableRowElement>, private _renderer: Renderer2) {
    const nativeElement = this._elementRef.nativeElement;
    this._renderer.addClass(nativeElement, 'sim-table-footer-row');
    this._renderer.setAttribute(nativeElement, 'role', 'row');
  }
}

export interface SimCellOutletRowContext<T> {
  /** Data for the row that this cell is located within. */
  $implicit?: T;

  /** Index of the data object in the provided data array. */
  index?: number;

  /** Length of the number of total rows. */
  count?: number;

  /** True if this cell is contained in the first row. */
  first?: boolean;

  /** True if this cell is contained in the last row. */
  last?: boolean;

  /** True if this cell is contained in a row with an even-numbered index. */
  even?: boolean;

  /** True if this cell is contained in a row with an odd-numbered index. */
  odd?: boolean;
}

export interface SimCellOutletMultiRowContext<T> {
  /** Data for the row that this cell is located within. */
  $implicit?: T;

  /** Index of the data object in the provided data array. */
  dataIndex?: number;

  /** Index location of the rendered row that this cell is located within. */
  renderIndex?: number;

  /** Length of the number of total rows. */
  count?: number;

  /** True if this cell is contained in the first row. */
  first?: boolean;

  /** True if this cell is contained in the last row. */
  last?: boolean;

  /** True if this cell is contained in a row with an even-numbered index. */
  even?: boolean;

  /** True if this cell is contained in a row with an odd-numbered index. */
  odd?: boolean;
}
